import {
  CATALOG_CROP_SOURCES,
  CATALOG_DRAFT_FLAGS,
  CATALOG_DRAFT_STATUSES,
  CATALOG_IMPORT_MODES,
  CATALOG_IMPORT_STATUSES,
  CATALOG_IMPORT_WAIT_REASONS,
  CATALOG_MEDIA_ROLES,
  CATALOG_PAGE_STATUSES,
  CATALOG_PAGE_TYPES,
  CatalogAttribute,
  CatalogBulkActionResult,
  CatalogCrop,
  CatalogCropResult,
  CatalogCropSource,
  CatalogCropVerification,
  CatalogDraft,
  CatalogDraftFlag,
  CatalogDraftMedia,
  CatalogDraftPage,
  CatalogDraftStatus,
  CatalogDuplicateOf,
  CatalogHeadings,
  CatalogImport,
  CatalogImportConfig,
  CatalogImportMode,
  CatalogImportProgress,
  CatalogImportStatus,
  CatalogImportWaitReason,
  CatalogMediaRole,
  CatalogPage,
  CatalogPageStatus,
  CatalogPageSummary,
  CatalogPageType,
  CatalogRegion,
  CatalogSectionMap,
  CatalogSkippedDraft,
  CatalogStoredError,
  CatalogUploadTarget,
  CatalogVariant,
  CreateCatalogImportResult,
  NormalizedBox,
} from "../../../interfaces/CatalogImportObject";
import { coerceSingle } from "../../helpers/ApiEnvelopeHelper";

// The catalogue import API is camelCase (Jackson defaults). These converters
// null-guard, coerce numbers and validate enum strings, so a partial or
// slightly-off response — or a value from a newer backend — can never crash a
// screen or render NaN. Unknown enum values become "UNKNOWN"; absent optional
// text becomes null; absent lists become [].

type Raw = Record<string, unknown>;

const asRecord = (value: unknown): Raw =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Raw) : {};

const toRequiredString = (value: unknown): string =>
  value === undefined || value === null ? "" : String(value);

const toNullableString = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const text = String(value);
  return text === "" ? null : text;
};

const toCount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

const toNullableCount = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : null;
};

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableBoolean = (value: unknown): boolean | null =>
  typeof value === "boolean" ? value : null;

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function toArray<T>(value: unknown, convert: (raw: unknown) => T): T[] {
  return Array.isArray(value) ? value.map(convert) : [];
}

function toNullableArray<T>(value: unknown, convert: (raw: unknown) => T): T[] | null {
  return Array.isArray(value) ? value.map(convert) : null;
}

const toStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v) => v !== null && v !== undefined).map(String) : [];

// ── Enums ────────────────────────────────────────────────────────────────────

const toImportStatus = (value: unknown): CatalogImportStatus =>
  oneOf(value, CATALOG_IMPORT_STATUSES, "UNKNOWN");
const toWaitReason = (value: unknown): CatalogImportWaitReason | null =>
  value === undefined || value === null ? null : oneOf(value, CATALOG_IMPORT_WAIT_REASONS, "UNKNOWN");
const toMode = (value: unknown): CatalogImportMode | null =>
  value === undefined || value === null ? null : oneOf(value, CATALOG_IMPORT_MODES, "UNKNOWN");
const toPageStatus = (value: unknown): CatalogPageStatus => oneOf(value, CATALOG_PAGE_STATUSES, "UNKNOWN");
const toPageType = (value: unknown): CatalogPageType => oneOf(value, CATALOG_PAGE_TYPES, "UNKNOWN");
const toDraftStatus = (value: unknown): CatalogDraftStatus =>
  oneOf(value, CATALOG_DRAFT_STATUSES, "UNKNOWN");
const toFlag = (value: unknown): CatalogDraftFlag => oneOf(value, CATALOG_DRAFT_FLAGS, "UNKNOWN");
const toMediaRole = (value: unknown): CatalogMediaRole => oneOf(value, CATALOG_MEDIA_ROLES, "UNKNOWN");
const toCropSource = (value: unknown): CatalogCropSource => oneOf(value, CATALOG_CROP_SOURCES, "UNKNOWN");

// ── Small shapes ─────────────────────────────────────────────────────────────

export function convertStoredError(raw: unknown): CatalogStoredError | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Raw;
  const code = toNullableString(r.code);
  const message = toNullableString(r.message);
  if (!code && !message) return null;
  return {
    code: code ?? "",
    message: message ?? "",
    retryable: r.retryable === true,
    at: toNullableString(r.at),
  };
}

export function convertBox(raw: unknown): NormalizedBox {
  const r = asRecord(raw);
  return { x: toNumber(r.x), y: toNumber(r.y), w: toNumber(r.w), h: toNumber(r.h) };
}

function convertAttribute(raw: unknown): CatalogAttribute {
  const r = asRecord(raw);
  return { key: toRequiredString(r.key), value: toRequiredString(r.value) };
}

export function convertProgress(raw: unknown): CatalogImportProgress {
  const r = asRecord(raw);
  return {
    pagesRendered: toCount(r.pagesRendered),
    pagesExtracted: toCount(r.pagesExtracted),
    pagesFailed: toCount(r.pagesFailed),
    draftsTotal: toCount(r.draftsTotal),
    draftsFlagged: toCount(r.draftsFlagged),
    draftsApproved: toCount(r.draftsApproved),
    draftsRejected: toCount(r.draftsRejected),
    draftsCreated: toCount(r.draftsCreated),
    draftsCommitFailed: toCount(r.draftsCommitFailed),
    stageStartedAt: toNullableString(r.stageStartedAt),
    estimatedReadyAt: toNullableString(r.estimatedReadyAt),
  };
}

export function convertConfig(raw: unknown): CatalogImportConfig {
  const r = coerceSingle(raw);
  return {
    enabled: r.enabled === true,
    maxFileBytes: toCount(r.maxFileBytes),
    maxPagesPerImport: toCount(r.maxPagesPerImport),
    realtimeMaxPages: toCount(r.realtimeMaxPages),
    remainingPagesToday: toCount(r.remainingPagesToday),
    retentionDays: toCount(r.retentionDays),
  };
}

function convertSectionMap(raw: unknown): CatalogSectionMap {
  const r = asRecord(raw);
  return {
    sectionMapId: toRequiredString(r.sectionMapId),
    sourceLabel: toRequiredString(r.sourceLabel),
    sectionId: toNullableString(r.sectionId),
    sectionName: toNullableString(r.sectionName),
    autoMatched: r.autoMatched === true,
    draftCount: toCount(r.draftCount),
  };
}

function convertPageSummary(raw: unknown): CatalogPageSummary {
  const r = asRecord(raw);
  return {
    pageNo: toCount(r.pageNo),
    status: toPageStatus(r.status),
    pageType: toPageType(r.pageType),
    thumbUrl: toNullableString(r.thumbUrl),
    draftCount: toCount(r.draftCount),
    flaggedCount: toCount(r.flaggedCount),
    approvedCount: toCount(r.approvedCount),
    error: convertStoredError(r.error),
  };
}

// ── Imports ──────────────────────────────────────────────────────────────────

/** One CatalogImportDTO. Accepts an array-wrapped single (`contents: [ {...} ]`). */
export function convertImport(raw: unknown): CatalogImport {
  const r = coerceSingle(raw);
  return {
    importId: toRequiredString(r.importId),
    businessId: toRequiredString(r.businessId),
    businessName: toNullableString(r.businessName),
    fileName: toRequiredString(r.fileName),
    fileSizeBytes: toCount(r.fileSizeBytes),
    pageCount: toNullableCount(r.pageCount),
    selectedPageCount: toNullableCount(r.selectedPageCount),
    pageRanges: toNullableString(r.pageRanges),
    instructions: toNullableString(r.instructions),
    status: toImportStatus(r.status),
    waitReason: toWaitReason(r.waitReason),
    mode: toMode(r.mode),
    progress: convertProgress(r.progress),
    submittedBy: toNullableString(r.submittedBy),
    submittedByName: toNullableString(r.submittedByName),
    createdAt: toNullableString(r.createdAt),
    startedAt: toNullableString(r.startedAt),
    readyAt: toNullableString(r.readyAt),
    completedAt: toNullableString(r.completedAt),
    cleanupAt: toNullableString(r.cleanupAt),
    error: convertStoredError(r.error),
    sectionMaps: toNullableArray(r.sectionMaps, convertSectionMap),
    pages: toNullableArray(r.pages, convertPageSummary),
  };
}

/** The list endpoint's `contents`: an array of CatalogImportDTO. Rows without an id are dropped. */
export function convertImportList(contents: unknown): CatalogImport[] {
  const rows = Array.isArray(contents)
    ? contents
    : Array.isArray(asRecord(contents).imports)
      ? (asRecord(contents).imports as unknown[])
      : [];
  return rows.map((row) => convertImport(row)).filter((row) => row.importId !== "");
}

function convertUploadTarget(raw: unknown): CatalogUploadTarget | null {
  const r = asRecord(raw);
  const uploadUrl = toNullableString(r.uploadUrl);
  if (!uploadUrl) return null;
  return {
    uploadUrl,
    path: toRequiredString(r.path ?? r.objectPath),
    publicUrl: toNullableString(r.publicUrl),
  };
}

export function convertCreateResult(raw: unknown): CreateCatalogImportResult {
  const r = coerceSingle(raw);
  return {
    importJob: convertImport(r.importJob ?? r.import),
    upload: convertUploadTarget(r.upload),
    uploadExpiresAt: toNullableString(r.uploadExpiresAt),
  };
}

// ── Pages, crops, drafts ─────────────────────────────────────────────────────

function convertRegion(raw: unknown): CatalogRegion {
  const r = asRecord(raw);
  return {
    regionId: toRequiredString(r.regionId),
    box: convertBox(r.box),
    source: toCropSource(r.source),
    usedByDraftIds: toStringList(r.usedByDraftIds),
  };
}

function convertVerification(raw: unknown): CatalogCropVerification | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Raw;
  return {
    singleProduct: toNullableBoolean(r.singleProduct),
    complete: toNullableBoolean(r.complete),
    matchesLabel: toNullableBoolean(r.matchesLabel),
    issue: toNullableString(r.issue),
  };
}

export function convertCrop(raw: unknown): CatalogCrop {
  const r = coerceSingle(raw);
  return {
    cropId: toRequiredString(r.cropId),
    pageNo: toCount(r.pageNo),
    box: convertBox(r.box),
    source: toCropSource(r.source),
    url: toNullableString(r.url),
    widthPx: toCount(r.widthPx),
    heightPx: toCount(r.heightPx),
    verification: convertVerification(r.verification),
  };
}

function convertDraftMedia(raw: unknown): CatalogDraftMedia {
  const r = asRecord(raw);
  return { cropId: toRequiredString(r.cropId), role: toMediaRole(r.role), url: toNullableString(r.url) };
}

function convertVariant(raw: unknown): CatalogVariant {
  const r = asRecord(raw);
  return {
    label: toRequiredString(r.label),
    code: toNullableString(r.code),
    cropId: toNullableString(r.cropId),
    url: toNullableString(r.url),
  };
}

function convertDuplicateOf(raw: unknown): CatalogDuplicateOf | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Raw;
  const productId = toNullableString(r.productId);
  if (!productId) return null;
  return { productId, title: toNullableString(r.title), thumbUrl: toNullableString(r.thumbUrl) };
}

/** One CatalogDraftDTO. Accepts an array-wrapped single. */
export function convertDraft(raw: unknown): CatalogDraft {
  const r = coerceSingle(raw);
  const confidence = toNumber(r.confidence, 0);
  return {
    draftId: toRequiredString(r.draftId),
    importId: toRequiredString(r.importId),
    status: toDraftStatus(r.status),
    flags: toArray(r.flags, toFlag),
    dismissedFlags: toArray(r.dismissedFlags, toFlag),
    confidence: Math.min(1, Math.max(0, confidence)),
    version: toCount(r.version),
    pageNos: Array.isArray(r.pageNos) ? r.pageNos.map((n) => toCount(n)).filter((n) => n > 0) : [],
    title: toNullableString(r.title),
    description: toNullableString(r.description),
    code: toNullableString(r.code),
    category: toNullableString(r.category),
    attributes: toArray(r.attributes, convertAttribute),
    sectionId: toNullableString(r.sectionId),
    sectionLabel: toNullableString(r.sectionLabel),
    media: toArray(r.media, convertDraftMedia),
    variants: toArray(r.variants, convertVariant),
    multiGroupId: toNullableString(r.multiGroupId),
    duplicateOf: convertDuplicateOf(r.duplicateOf),
    productId: toNullableString(r.productId),
    error: convertStoredError(r.error),
    reviewedBy: toNullableString(r.reviewedBy),
    reviewedAt: toNullableString(r.reviewedAt),
  };
}

export function convertDraftList(contents: unknown): CatalogDraft[] {
  return (Array.isArray(contents) ? contents : [])
    .map((row) => convertDraft(row))
    .filter((draft) => draft.draftId !== "");
}

/** GET .../pages/{pageNo}. */
export function convertPage(raw: unknown): CatalogPage {
  const r = coerceSingle(raw);
  const headings = r.headings && typeof r.headings === "object" ? (r.headings as Raw) : null;
  const convertedHeadings: CatalogHeadings | null = headings
    ? { collection: toNullableString(headings.collection), series: toNullableString(headings.series) }
    : null;
  return {
    pageNo: toCount(r.pageNo),
    status: toPageStatus(r.status),
    pageType: toPageType(r.pageType),
    imageUrl: toNullableString(r.imageUrl),
    thumbUrl: toNullableString(r.thumbUrl),
    widthPx: toCount(r.widthPx),
    heightPx: toCount(r.heightPx),
    headings: convertedHeadings,
    sharedSpecs: toArray(r.sharedSpecs, convertAttribute),
    regions: toArray(r.regions, convertRegion),
    crops: toArray(r.crops, convertCrop),
    drafts: convertDraftList(r.drafts),
    error: convertStoredError(r.error),
    urlsExpireAt: toNullableString(r.urlsExpireAt),
  };
}

/** GET .../drafts: `{ drafts, nextCursor }` (or a bare array of drafts). */
export function convertDraftPage(raw: unknown): CatalogDraftPage {
  if (Array.isArray(raw) && raw.length > 0 && !("drafts" in asRecord(raw[0]))) {
    return { drafts: convertDraftList(raw), nextCursor: null };
  }
  const r = coerceSingle(raw);
  return { drafts: convertDraftList(r.drafts), nextCursor: toNullableString(r.nextCursor) };
}

function convertSkipped(raw: unknown): CatalogSkippedDraft {
  const r = asRecord(raw);
  return { draftId: toRequiredString(r.draftId), reason: toRequiredString(r.reason) };
}

export function convertBulkResult(raw: unknown): CatalogBulkActionResult {
  const r = coerceSingle(raw);
  return {
    updated: toCount(r.updated),
    skipped: toArray(r.skipped, convertSkipped),
    progress: r.progress && typeof r.progress === "object" ? convertProgress(r.progress) : null,
  };
}

/**
 * Drafts returned by merge / split. Accepts a bare list, `{ drafts: [...] }`,
 * or a single draft — the contract only says "the resulting drafts".
 */
export function convertDraftsChange(raw: unknown): CatalogDraft[] {
  if (Array.isArray(raw)) {
    if (raw.length === 1 && Array.isArray(asRecord(raw[0]).drafts)) {
      return convertDraftList(asRecord(raw[0]).drafts);
    }
    return convertDraftList(raw);
  }
  const r = asRecord(raw);
  if (Array.isArray(r.drafts)) return convertDraftList(r.drafts);
  return r.draftId ? [convertDraft(r)] : [];
}

/** POST .../crops: `{ crop?, draft? }` or the crop itself. */
export function convertCropResult(raw: unknown): CatalogCropResult {
  const r = coerceSingle(raw);
  if (r.crop || r.draft) {
    return {
      crop: r.crop ? convertCrop(r.crop) : null,
      draft: r.draft ? convertDraft(r.draft) : null,
    };
  }
  return { crop: r.cropId ? convertCrop(r) : null, draft: null };
}

export default {
  convertImport,
  convertImportList,
  convertCreateResult,
  convertConfig,
  convertPage,
  convertDraft,
  convertDraftPage,
  convertBulkResult,
  convertDraftsChange,
  convertCropResult,
};
